import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Search, Database } from 'lucide-react';
import { LoadingSpinner } from '../../../../common';
import { AdminEmptyState } from '../../../ui';
import { Agent } from '../../../../../types/agent';
import { DataSource } from '../../../../../types/dataSource';
import { PaginationMetadata } from '../../../../../types/user';
import * as agentService from '../../../../../services/agentService';
import * as dataSourceService from '../../../../../services/datasourceService';

interface AgentDataSourceProps {
  agent: Agent;
  onUpdate: () => void;
}

interface DataSourceCardProps {
  ds: DataSource;
  isConnected?: boolean;
  onAdd?: () => void;
  onRemove?: () => void;
  onClick?: () => void;
}

const DataSourceCard: React.FC<DataSourceCardProps> = ({
  ds,
  isConnected,
  onAdd,
  onRemove,
  onClick,
}) => {
  return (
    <div
      className={`
        bg-[#1a1a1a] rounded-xl p-4
        border transition-all duration-200
        ${isConnected
          ? 'border-[#F4CC04]/30'
          : 'border-[#333333] hover:border-[#F4CC04] cursor-pointer'
        }
      `}
      onClick={!isConnected ? onAdd : undefined}
    >
      <div className="flex items-start justify-between gap-3">
        <div
          className={isConnected ? 'cursor-pointer flex-1' : 'flex-1'}
          onClick={isConnected ? onClick : undefined}
        >
          <h3 className="text-white font-medium text-sm line-clamp-1">
            {ds.name}
          </h3>
          <p className="text-[#888888] text-xs mt-1 line-clamp-2">
            {ds.description || 'Sem descrição'}
          </p>
        </div>
        {isConnected ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove?.();
            }}
            className="p-1.5 text-[#888888] hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
            title="Remover data source"
          >
            <X className="w-4 h-4" />
          </button>
        ) : (
          <div className="p-1.5 text-[#F4CC04]">
            <Plus className="w-4 h-4" />
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#333333]">
        <Database className="w-3 h-3 text-[#888888]" />
        <span className="text-xs text-[#888888]">
          {ds.rag_type || 'SQL'}
        </span>
      </div>
    </div>
  );
};

export const AgentDataSource: React.FC<AgentDataSourceProps> = ({ agent, onUpdate }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [connectedDataSources, setConnectedDataSources] = useState<DataSource[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationData, setPaginationData] = useState<PaginationMetadata | null>(null);

  const pageLimit = 10;

  const fetchConnectedDataSources = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);

      const connectedResponse = await agentService.getAgentDataSources(agent.id);

      if (connectedResponse.success && connectedResponse.data) {
        const dataSources = connectedResponse.data
          .filter((dsResponse) => dsResponse.success && dsResponse.data)
          .map((dsResponse) => dsResponse.data!);
        setConnectedDataSources(dataSources);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar fontes de dados');
    } finally {
      setIsLoading(false);
    }
  }, [agent.id]);

  const fetchDataSources = useCallback(
    async (page: number = 1) => {
      try {
        setError(null);
        if (page === 1) {
          setIsLoading(true);
        } else {
          setIsLoadingMore(true);
        }

        setCurrentPage(page);

        // Never filter DS listing by the agent's workspace.
        // The admin should see data sources from their own workspace (TD admin → workspace 5,
        // workspace admin → their workspace), not the agent's client workspace.
        const response = await dataSourceService.listDataSourcesWithPagination(
          page,
          pageLimit,
          debouncedSearchQuery,
          undefined,
          undefined
        );

        if (response.success) {
          if (page === 1) {
            setDataSources(response.data);
          } else {
            setDataSources((prev) => [...prev, ...response.data]);
          }
          setPaginationData(response.metadata);
        } else {
          setError(response.message || 'Falha ao carregar fontes de dados');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Falha ao carregar fontes de dados');
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [pageLimit, debouncedSearchQuery, agent.id]
  );

  useEffect(() => {
    fetchConnectedDataSources();
  }, [fetchConnectedDataSources]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (showAddModal) {
      fetchDataSources(1);
    }
  }, [debouncedSearchQuery, showAddModal, fetchDataSources]);

  const handleLoadMore = () => {
    if (paginationData?.has_more && !isLoadingMore) {
      fetchDataSources(currentPage + 1);
    }
  };

  const handleDataSourceAdd = async (dsId: number) => {
    try {
      setError(null);
      await dataSourceService.addAgent(dsId, agent.id);
      await fetchConnectedDataSources();
      onUpdate();
      setShowAddModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao adicionar fonte de dados');
    }
  };

  const handleDataSourceRemove = async (dsId: number) => {
    try {
      setError(null);
      await dataSourceService.removeAgent(dsId, agent.id);
      await fetchConnectedDataSources();
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao remover fonte de dados');
    }
  };

  const handleAddModalOpen = () => {
    setShowAddModal(true);
    fetchDataSources(1);
  };

  const filteredDataSources = dataSources.filter(
    (ds) => !connectedDataSources.some((connectedDs) => connectedDs.id === ds.id)
  );

  if (isLoading && !showAddModal) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Data Sources Conectados</h2>
        <button
          onClick={handleAddModalOpen}
          className="
            flex items-center gap-2 px-3 py-2
            bg-[#F4CC04] hover:bg-[#D4B104]
            text-black text-sm font-medium
            rounded-lg transition-colors
          "
        >
          <Plus className="w-4 h-4" />
          Adicionar
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Connected Data Sources */}
      {connectedDataSources.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {connectedDataSources.map((ds) => (
            <DataSourceCard
              key={ds.id}
              ds={ds}
              isConnected
              onRemove={() => handleDataSourceRemove(ds.id)}
              onClick={() => navigate(`/admin/datasources/${ds.id}`)}
            />
          ))}
        </div>
      ) : (
        <AdminEmptyState
          title="Nenhum Data Source Conectado"
          description="Adicione data sources para melhorar as capacidades do agente"
          action={
            <button
              onClick={handleAddModalOpen}
              className="px-4 py-2 bg-[#F4CC04] hover:bg-[#D4B104] text-black font-medium rounded-lg transition-colors"
            >
              Adicionar Data Source
            </button>
          }
        />
      )}

      {/* Add Modal */}
      {showAddModal && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-40"
            onClick={() => setShowAddModal(false)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-[#2a2a2a] rounded-2xl p-6 z-50 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Adicionar Data Source</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <input
                type="text"
                placeholder="Buscar data sources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="
                  w-full px-4 py-2.5 pl-10
                  bg-[#1a1a1a] border border-[#333333]
                  text-white placeholder-[#888888] text-sm
                  rounded-lg
                  focus:outline-none focus:border-[#F4CC04]
                "
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888888]" />
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto space-y-3 min-h-[200px]">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="medium" />
                </div>
              ) : filteredDataSources.length > 0 ? (
                <>
                  {filteredDataSources.map((ds) => (
                    <DataSourceCard
                      key={ds.id}
                      ds={ds}
                      onAdd={() => handleDataSourceAdd(ds.id)}
                    />
                  ))}

                  {/* Load More */}
                  {paginationData?.has_more && (
                    <div className="flex justify-center pt-4">
                      {isLoadingMore ? (
                        <LoadingSpinner size="small" />
                      ) : (
                        <button
                          onClick={handleLoadMore}
                          className="px-4 py-2 bg-[#333333] hover:bg-[#444444] text-white text-sm rounded-lg transition-colors"
                        >
                          Carregar Mais
                        </button>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-[#888888] text-sm">Nenhum data source encontrado</p>
                  <p className="text-[#666666] text-xs mt-1">Tente ajustar os termos de busca</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AgentDataSource;
